import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const GOLD = '#d4af37';

interface EventItem {
  id: string;
  eventName: string;
  date: string;
  description: string;
  imageUrl: string;
  category: string;
}

interface EventsContent {
  title: string;
  description: string;
  category: string;
  events: EventItem[];
}

const EventsGalleryManager = () => {
  const [content, setContent] = useState<EventsContent>({
    title: 'Temple Events Gallery',
    description: 'Gallery of temple events and celebrations',
    category: 'events',
    events: [
      { id: '1', eventName: 'Ulsav Festival 2024', date: '2024-05-01', description: 'Annual temple festival', imageUrl: 'https://via.placeholder.com/400x300?text=Event+1', category: 'ulsavam' },
      { id: '2', eventName: 'Shivaratri Celebration', date: '2024-03-15', description: 'Maha Shivaratri celebration', imageUrl: 'https://via.placeholder.com/400x300?text=Event+2', category: 'festival' },
    ]
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'gallery_content', 'events'), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as Partial<EventsContent>;
          setContent((prev) => {
            const nextEvents = (data.events ?? prev.events).map((event: any) => ({
              ...event,
              category: event.category || 'general'
            }));
            return { ...prev, ...data, events: nextEvents };
          });
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'gallery_content', 'events'), content);
      showSnackbar('Events gallery saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddEvent = () => {
    const newEvent: EventItem = {
      id: Date.now().toString(),
      eventName: 'New Event',
      date: new Date().toISOString().split('T')[0],
      description: '',
      imageUrl: '',
      category: 'general'
    };
    setContent({
      ...content,
      events: [...content.events, newEvent]
    });
  };

  const handleEventChange = (id: string, field: keyof EventItem, value: string) => {
    setContent({
      ...content,
      events: content.events.map(event =>
        event.id === id ? { ...event, [field]: value } : event
      )
    });
  };

  const handleDeleteEvent = (id: string) => {
    setContent({
      ...content,
      events: content.events.filter(event => event.id !== id)
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Events Gallery Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage temple events gallery content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          ⚙️ Gallery Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Gallery Title"
              value={content.title}
              onChange={(e) => setContent({ ...content, title: e.target.value })}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Gallery Description"
              value={content.description}
              onChange={(e) => setContent({ ...content, description: e.target.value })}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Category (Future Filter)"
              value={content.category}
              onChange={(e) => setContent({ ...content, category: e.target.value })}
              size="small"
              placeholder="events"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD }}>
            📅 Events ({content.events.length})
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddEvent}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            Add Event
          </Button>
        </Box>

        <Grid container spacing={2}>
          {content.events.map((event) => (
            <Grid item xs={12} key={event.id}>
              <Paper sx={{ p: 2, bgcolor: '#fff', border: `1px solid ${GOLD}` }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Event Name"
                      value={event.eventName}
                      onChange={(e) => handleEventChange(event.id, 'eventName', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Event Date"
                      type="date"
                      value={event.date}
                      onChange={(e) => handleEventChange(event.id, 'date', e.target.value)}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        label="Category"
                        value={event.category || 'general'}
                        onChange={(e) => handleEventChange(event.id, 'category', e.target.value)}
                      >
                        <MenuItem value="general">General</MenuItem>
                        <MenuItem value="ulsavam">Ulsavam</MenuItem>
                        <MenuItem value="festival">Festival</MenuItem>
                        <MenuItem value="pooja">Pooja</MenuItem>
                        <MenuItem value="community">Community</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => handleDeleteEvent(event.id)}
                      color="error"
                      sx={{ width: '100%' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={event.imageUrl}
                      onChange={(e) => handleEventChange(event.id, 'imageUrl', e.target.value)}
                      size="small"
                      placeholder="https://..."
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={event.description}
                      onChange={(e) => handleEventChange(event.id, 'description', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EventsGalleryManager;
